
import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Card, CardContent, Chip, CircularProgress, Container, createTheme, Divider, FormControl, Grid, InputAdornment, InputLabel, MenuItem, Paper, Select, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, ThemeProvider, Typography, useMediaQuery } from '@mui/material';
// import SchoolList from '../view-model/SchoolList';
import authService from '../Auth/AuthService';

const theme = createTheme({
    palette: {
        primary: { main: '#2563eb' },
        secondary: { main: '#64748b' },
        background: { default: '#f5f7fb' }
    },
    typography: {
        fontFamily: '"Inter", sans-serif',
        h5: { fontWeight: 600, letterSpacing: '-0.02em' },
        body2: { fontSize: '0.875rem' }
    },
    shape: { borderRadius: 12 },
    components: {
        MuiTableCell: {
            styleOverrides: {
                root: {
                    borderBottom: '1px solid #f1f5f9',
                    padding: '14px 12px',
                    fontSize: '0.8rem'
                }
            }
        },
        MuiChip: {
            styleOverrides: {
                root: { borderRadius: '30px', fontWeight: 500, fontSize: '0.7rem' }
            }
        }
    }
});

const SchoolPage = () => {
    const [schools, setSchools] = useState([]);
    const DOC_URL = authService.DOC_URL
    const [loading, setLoading] = useState(true);
    const hasFetched = useRef(false);

    useEffect(() => {
        const fetchLoginData = async () => {
            if (hasFetched.current) return;
            hasFetched.current = true;
            try {
                const url = authService.pathUrl("GET", "/schools");
                const data = await authService.getAuth(url);
                setSchools(
                    data.map(d => ({
                        ...d,
                        logoUrl: d.logoUrl ? DOC_URL + d.logoUrl : null
                    }))
                );
            } catch (error) {
                console.error("Error fetching login data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchLoginData();
    }, []);

    function SchoolList({ schoolData }) {
        const isTablet = useMediaQuery(theme.breakpoints.down('md'));
        const [page, setPage] = React.useState(0);
        const [rowsPerPage, setRowsPerPage] = React.useState(5);

        // Search/filter states
        const [searchTerm, setSearchTerm] = React.useState('');
        const [filterField, setFilterField] = React.useState('schoolName');

        // Filter data based on search term and field
        const filteredData = React.useMemo(() => {
            if (!searchTerm.trim()) return schoolData;

            const term = searchTerm.trim().toLowerCase();
            return schoolData.filter(school => {
                let value = '';
                switch (filterField) {
                    case 'schoolName':
                        value = school.schoolName.toLowerCase();
                        break;
                    case 'principalName':
                        value = school.principalName.toLowerCase();
                        break;
                    case 'contactPhone':
                        value = school.contactPhone.toLowerCase();
                        break;
                    case 'totalStudents':
                        value = school.totalStudents.toString();
                        break;
                    default:
                        value = '';
                }
                return value.includes(term);
            });
        }, [searchTerm, filterField]);

        // Reset page when filter changes
        React.useEffect(() => {
            setPage(0);
        }, [searchTerm, filterField]);

        // Pagination logic
        const emptyRows = Math.max(0, (1 + page) * rowsPerPage - filteredData.length);
        const paginatedData = filteredData.slice(
            page * rowsPerPage,
            page * rowsPerPage + rowsPerPage
        );

        const handleChangePage = (event, newPage) => {
            setPage(newPage);
        };

        const handleChangeRowsPerPage = (event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
        };

        // helper: format date
        const formatDate = (dateStr) => {
            if (!dateStr) return '—';
            const d = new Date(dateStr);
            return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
        };

        // ----- Desktop Table -----
        const renderTable = () => (
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '16px', border: '1px solid #f1f5f9' }}>
                <Table sx={{ minWidth: 900 }}>
                    <TableHead sx={{ backgroundColor: '#f8fafc' }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 600, color: '#1e293b' }}>School Name</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#1e293b' }}>Principal</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#1e293b' }}>Contact</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#1e293b' }}>Address</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#1e293b' }}>Details</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#1e293b' }}>Status</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedData.map((school) => (
                            <TableRow key={school.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                <TableCell>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
                                        {school.schoolName}
                                    </Typography>
                                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.3 }}>
                                        {school.schoolType} · {school.boardAffiliation}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{school.principalName}</Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2">{school.contactPhone}</Typography>
                                    <Typography variant="caption" display="block" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                                        {school.contactEmail}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Box className="address-block">
                                        <div>{school.addressDTO.addressLine1}</div>
                                        <div>{school.addressDTO.city}, {school.addressDTO.state} - {school.addressDTO.pincode}</div>
                                        <div style={{ fontSize: '0.65rem', color: '#64748b', marginTop: '2px' }}>
                                            <i className="fas fa-map-pin" style={{ marginRight: '4px' }}></i>
                                            {school.addressDTO.latitude}, {school.addressDTO.longitude}
                                        </div>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Box className="info-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
                                        <div className="info-item"><i className="fas fa-user-graduate"></i> {school.totalStudents} students</div>
                                        <div className="info-item"><i className="fas fa-chalkboard-teacher"></i> {school.totalTeachers} teachers</div>
                                        <div className="info-item"><i className="fas fa-layer-group"></i> {school.totalClasses} classes</div>
                                        <div className="info-item"><i className="fas fa-calendar-alt"></i> Est. {school.establishedYear}</div>
                                        <div className="info-item" style={{ gridColumn: 'span 2' }}>
                                            <i className="fas fa-language"></i> {school.mediumOfInstruction}
                                        </div>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={school.isActive ? 'Active' : 'Inactive'}
                                        className={school.isActive ? 'chip-active' : 'chip-inactive'}
                                        size="small"
                                        icon={school.isActive ? <i className="fas fa-circle" style={{ fontSize: '0.5rem', color: '#2e7d32' }}></i> : <i className="fas fa-circle" style={{ fontSize: '0.5rem', color: '#c62828' }}></i>}
                                    />
                                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                                        <i className="far fa-clock" style={{ marginRight: '4px' }}></i>
                                        {formatDate(school.updatedAt)}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ))}
                        {emptyRows > 0 && (
                            <TableRow style={{ height: 53 * emptyRows }}>
                                <TableCell colSpan={6} />
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={filteredData.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{ borderTop: '1px solid #f1f5f9' }}
                />
            </TableContainer>
        );

        // ----- Mobile / Card view -----
        const renderCards = () => (
            <>
                <Grid container spacing={2}>
                    {paginatedData.map((school) => (
                        <Grid item xs={12} key={school.id}>
                            <Card className="card-hover" sx={{ borderRadius: '16px', border: '1px solid #f1f5f9', boxShadow: 'none' }}>
                                <CardContent>
                                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                                        <Box>
                                            <Typography variant="subtitle1" className="school-name" sx={{ fontSize: '1rem' }}>
                                                {school.schoolName}
                                            </Typography>
                                            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.3 }}>
                                                {school.schoolType} · {school.boardAffiliation}
                                            </Typography>
                                        </Box>
                                        <Chip
                                            label={school.isActive ? 'Active' : 'Inactive'}
                                            className={school.isActive ? 'chip-active' : 'chip-inactive'}
                                            size="small"
                                        />
                                    </Stack>

                                    <Divider sx={{ my: 1.5 }} />

                                    <Grid container spacing={1} sx={{ mb: 1 }}>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">Principal</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{school.principalName}</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">Contact</Typography>
                                            <Typography variant="body2">{school.contactPhone}</Typography>
                                            <Typography variant="caption" display="block" color="text.secondary" noWrap>{school.contactEmail}</Typography>
                                        </Grid>
                                    </Grid>

                                    <Box sx={{ backgroundColor: '#f8fafc', p: 1.2, borderRadius: '10px', mb: 1.5 }}>
                                        <Typography variant="caption" color="text.secondary">Address</Typography>
                                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                            {school.addressDTO.addressLine1}
                                            {school.addressDTO.addressLine2 && `, ${school.addressDTO.addressLine2}`}
                                            <br />
                                            {school.addressDTO.city}, {school.addressDTO.state} - {school.addressDTO.pincode}
                                        </Typography>
                                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.3 }}>
                                            <i className="fas fa-map-pin"></i> {school.addressDTO.latitude}, {school.addressDTO.longitude}
                                        </Typography>
                                    </Box>

                                    <Grid container spacing={1} sx={{ mb: 1 }}>
                                        <Grid item xs={4}><Typography variant="caption" color="text.secondary">Students</Typography><Typography variant="body2">{school.totalStudents}</Typography></Grid>
                                        <Grid item xs={4}><Typography variant="caption" color="text.secondary">Teachers</Typography><Typography variant="body2">{school.totalTeachers}</Typography></Grid>
                                        <Grid item xs={4}><Typography variant="caption" color="text.secondary">Classes</Typography><Typography variant="body2">{school.totalClasses}</Typography></Grid>
                                        <Grid item xs={6}><Typography variant="caption" color="text.secondary">Established</Typography><Typography variant="body2">{school.establishedYear}</Typography></Grid>
                                        <Grid item xs={6}><Typography variant="caption" color="text.secondary">Medium</Typography><Typography variant="body2">{school.mediumOfInstruction}</Typography></Grid>
                                    </Grid>

                                    <Stack direction="row" flexWrap="wrap" spacing={1} sx={{ mt: 0.5 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                                            <i className="far fa-clock"></i> {formatDate(school.updatedAt)}
                                        </Typography>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={filteredData.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        sx={{ border: 'none', width: '100%' }}
                    />
                </Box>
            </>
        );

        // ----- main render -----
        return (
            <ThemeProvider theme={theme}>
                <Box className="mui-container" sx={{ py: { xs: 1, sm: 2 } }}>
                    {/* Header with search and filter */}
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a', fontSize: { xs: '1.3rem', sm: '1.8rem' } }}>
                            <i className="fas fa-school" style={{ marginRight: '12px', color: '#2563eb' }}></i>
                            Schools
                        </Typography>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                            <FormControl size="small" sx={{ minWidth: 150 }}>
                                <InputLabel>Search by</InputLabel>
                                <Select
                                    value={filterField}
                                    label="Search by"
                                    onChange={(e) => setFilterField(e.target.value)}
                                >
                                    <MenuItem value="schoolName">School Name</MenuItem>
                                    <MenuItem value="principalName">Principal Name</MenuItem>
                                    <MenuItem value="contactPhone">Contact Phone</MenuItem>
                                    <MenuItem value="totalStudents">Total Students</MenuItem>
                                </Select>
                            </FormControl>

                            <TextField
                                size="small"
                                placeholder={`Search ${filterField === 'schoolName' ? 'schools' : filterField === 'principalName' ? 'principals' : filterField === 'contactPhone' ? 'phone numbers' : 'student count'}...`}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                sx={{
                                    flex: 1,
                                    minWidth: { xs: '100%', sm: '250px' },
                                    '& .MuiOutlinedInput-root': { borderRadius: '30px' }
                                }}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <i className="fas fa-search" style={{ color: '#64748b' }}></i>
                                            </InputAdornment>
                                        ),
                                        endAdornment: searchTerm && (
                                            <InputAdornment position="end">
                                                <Button
                                                    size="small"
                                                    onClick={() => setSearchTerm('')}
                                                    sx={{ minWidth: 'auto', p: 0, color: '#64748b' }}
                                                >
                                                    <i className="fas fa-times"></i>
                                                </Button>
                                            </InputAdornment>
                                        )
                                    }
                                }}
                            />
                        </Stack>
                    </Stack>

                    {/* Results count */}
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Found {filteredData.length} school{filteredData.length !== 1 ? 's' : ''}
                        {searchTerm && ` matching "${searchTerm}"`}
                    </Typography>

                    {/* responsive switch: table or cards */}
                    {isTablet ? renderCards() : renderTable()}

                    {/* footer meta */}
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Typography variant="caption" color="text.secondary">
                            Showing {paginatedData.length} of {filteredData.length} schools · Page {page + 1}
                        </Typography>
                    </Box>
                </Box>
            </ThemeProvider>
        );
    }


    if (loading) {
        return (
            <Box display="flex" justifyContent="center" mt={5}>
                <CircularProgress />
            </Box>
        );
    }
    return (
        <Container >
            {/* <Typography variant="h4" gutterBottom>School Management</Typography> */}
            <SchoolList schoolData={schools} />
        </Container>
    );
};

export default SchoolPage;
